import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import _get from 'lodash/get';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import { IconName } from '@lib/components/Modal/enums';
import { DOCUMENT_ANALYTICS_FILTER_FORM } from '@lib/enums/localStorageKeys';
import { Value } from '@lib/interfaces/form';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  useEdmDocumentationsIdsQuery,
} from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { useWhoamiContext } from 'hooks/useWhoamiContext';
import AnalyticsPeriodFilter from './AnalyticsPeriodFilter/AnalyticsPeriodFilter';
import TopDocumentsByViewsTable from './TopDocumentsByViewsTable/TopDocumentsByViewsTable';
import DocumentsViewingTrend from './DocumentsViewingTrend/DocumentsViewingTrend';
import TopCategoriesByViews from './TopCategoriesByViews/TopCategoriesByViews';
import getDefaultAnalyticsPeriod from './utils/getDefaultAnalyticsPeriod';
import styles from './DocumentAnalytics.module.scss';

const PERIOD_FIELD = 'period' as const;

const EMPTY_FILTER_VALUES: { [key: string]: Value } = {
  [PERIOD_FIELD]: undefined,
};

export default function DocumentAnalyticsContent() {
  const { t } = useTranslation();
  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isCategoriesAnalyticsLocked =
    getUserAccessByComponent(
      AccessComponentEnum.DocumentationCategoriesManagement,
    ) === AccessLevelEnum.LimitedRead;

  // Lightweight check: fetch 1 document ID to know whether any documents exist.
  const documentCheckQuery = useEdmDocumentationsIdsQuery({
    fetchPolicy: 'cache-and-network',
    variables: { page: 1, limit: 1 },
  });
  const documentCheckCollection = _get(documentCheckQuery, [
    'data',
    'data',
    'collection',
  ]) as { id: string }[] | undefined;
  // Only treat the page as empty once the query has actually returned data;
  // otherwise the `loading=false / data=undefined` window flashes the empty state.
  const hasNoDocuments =
    !documentCheckQuery.loading &&
    Array.isArray(documentCheckCollection) &&
    documentCheckCollection.length === 0;

  // Default period mirrors Monitoring: company.createdAt → today.
  // Reads ProtectedRoute's single whoami query result — see useWhoamiContext.
  const { currentUser } = useWhoamiContext();
  const companyCreatedAt = _get(currentUser, 'company.createdAt') as
    string | undefined;
  const defaultPeriod = useMemo(
    () => getDefaultAnalyticsPeriod(companyCreatedAt),
    [companyCreatedAt],
  );

  // Persisted in localStorage like the Monitoring filter.
  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(DOCUMENT_ANALYTICS_FILTER_FORM, EMPTY_FILTER_VALUES);

  const storedPeriod = filterParams[PERIOD_FIELD] as string[] | undefined;
  const period =
    Array.isArray(storedPeriod) && storedPeriod[0] && storedPeriod[1]
      ? storedPeriod
      : defaultPeriod;

  const setPeriod = useCallback(
    (next: string[] | null | undefined) => {
      // Clearing the picker should restore the default range, never leave empty.
      const safe =
        Array.isArray(next) && next[0] && next[1] ? next : defaultPeriod;
      setFilterParams((prev) => ({ ...prev, [PERIOD_FIELD]: safe }));
    },
    [defaultPeriod, setFilterParams],
  );

  const isDefaultPeriod =
    period[0] === defaultPeriod[0] && period[1] === defaultPeriod[1];

  const onClearPeriod = useCallback(
    () => setPeriod(defaultPeriod),
    [setPeriod, defaultPeriod],
  );

  // Coordinate a single page-level loader across every sub-component query.
  // Each chart calls `onFirstLoaded` once its primary + join queries resolve
  // for the first time; until everyone reports back, we show one CircleLoader
  // instead of N separate per-card loaders.
  const expectedReadyCount = isCategoriesAnalyticsLocked ? 2 : 3;
  const [readyCount, setReadyCount] = useState(0);
  const handleChildReady = useCallback(() => {
    setReadyCount((prev) => prev + 1);
  }, []);

  if (hasNoDocuments) {
    return (
      <PageBody>
        <EmptyState
          icon={IconName.SearchOff}
          title={t('no-analytics-available-yet')}
          text={
            <>
              {t('no-analytics-available-yet-text')}
              <br />
              {t('no-analytics-available-yet-text-2')}
            </>
          }
        />
      </PageBody>
    );
  }

  const isInitialLoading =
    (documentCheckQuery.loading && !documentCheckQuery.data) ||
    readyCount < expectedReadyCount;

  return (
    <div className={styles.gatedContent}>
      {isInitialLoading && (
        <PageBody>
          <CircleLoader />
        </PageBody>
      )}
      <div className={isInitialLoading ? styles.gatedContentHidden : undefined}>
        <AnalyticsPeriodFilter value={period} onChange={setPeriod} />
        <TopDocumentsByViewsTable
          period={period}
          isDefaultPeriod={isDefaultPeriod}
          onClearPeriod={onClearPeriod}
          onFirstLoaded={handleChildReady}
        />
        <DocumentsViewingTrend
          period={period}
          isDefaultPeriod={isDefaultPeriod}
          onClearPeriod={onClearPeriod}
          onFirstLoaded={handleChildReady}
        />
        {!isCategoriesAnalyticsLocked && (
          <TopCategoriesByViews
            period={period}
            isDefaultPeriod={isDefaultPeriod}
            onClearPeriod={onClearPeriod}
            onFirstLoaded={handleChildReady}
          />
        )}
      </div>
    </div>
  );
}
